'use client'

import { Suspense, useEffect, useMemo, useState } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { getAccount } from '@/lib/auth'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid
} from 'recharts'

function startOfDayISO(d) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x.toISOString()
}
function endOfDayISO(d) {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x.toISOString()
}
function fmtDay(t) {
  try {
    const d = new Date(t)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return t
  }
}
function fmtDateTime(t) {
  if (!t) return '-'
  try {
    const d = new Date(t)
    return d.toLocaleString()
  } catch {
    return t
  }
}
function clampInterval(from, to) {
  try {
    const f = new Date(from)
    const tt = new Date(to)
    const days = (tt - f) / (1000 * 60 * 60 * 24)
    return days <= 2 ? 'hour' : 'day'
  } catch {
    return 'day'
  }
}
function cls(...args) {
  return args.filter(Boolean).join(' ')
}

function AnalyticsContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()

  // Auth
  const [user, setUser] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)

  // Filters
  const [fromISO, setFromISO] = useState(() => {
    const now = new Date()
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    return startOfDayISO(sevenDaysAgo)
  })
  const [toISO, setToISO] = useState(() => endOfDayISO(new Date()))
  const [interval, setInterval] = useState('day')
  const [automationKey, setAutomationKey] = useState('')
  const [msgStatus, setMsgStatus] = useState('') // '', 'queued', 'sent', 'failed'
  const [msgReply, setMsgReply] = useState('') // '', 'yes', 'no'
  const [msgPhone, setMsgPhone] = useState('')
  const [msgUserId, setMsgUserId] = useState('')
  const [deliveredOnly, setDeliveredOnly] = useState(false)
  const [msgDay, setMsgDay] = useState('') // yyyy-MM-dd
  const [limit, setLimit] = useState(50)

  // Twilio filters
  const [twilioToPhone, setTwilioToPhone] = useState('')
  const [twilioStatus, setTwilioStatus] = useState('')
  const [twilioDirection, setTwilioDirection] = useState('')
  const [twilioLimit, setTwilioLimit] = useState(500)

  // Data
  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState(null)
  const [timeseries, setTimeseries] = useState(null)
  const [twilioAgg, setTwilioAgg] = useState(null)

  const [messages, setMessages] = useState([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesNextCursor, setMessagesNextCursor] = useState(null)
  const [messagesError, setMessagesError] = useState('')
  const [sortField, setSortField] = useState('when') // when|status|delivered|reply|automation|phone|user|error
  const [sortDir, setSortDir] = useState('desc') // asc|desc

  // Populate from URL on first render
  useEffect(() => {
    const f = searchParams.get('from')
    const t = searchParams.get('to')
    const ak = searchParams.get('automationKey')
    const st = searchParams.get('status')
    const ph = searchParams.get('phone')
    const uid = searchParams.get('userId')
    const itv = searchParams.get('interval')
    const rep = searchParams.get('reply')
    const del = searchParams.get('delivered')
    const tlTo = searchParams.get('tl_to')
    const tlSt = searchParams.get('tl_status')
    const tlDir = searchParams.get('tl_dir')
    const srt = searchParams.get('sort')
    const ord = searchParams.get('order')

    if (f) setFromISO(f)
    if (t) setToISO(t)
    if (ak) setAutomationKey(ak)
    if (st !== null) setMsgStatus(st)
    if (rep !== null) setMsgReply(rep)
    if (ph) setMsgPhone(ph)
    if (uid) setMsgUserId(uid)
    if (itv) setInterval(itv)
    if (del !== null) setDeliveredOnly(del === '1' || del === 'true')

    if (tlTo) setTwilioToPhone(tlTo)
    if (tlSt !== null) setTwilioStatus(tlSt)
    if (tlDir !== null) setTwilioDirection(tlDir)
    if (srt) setSortField(srt)
    if (ord) setSortDir((ord === 'asc' || ord === 'desc') ? ord : 'desc')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep URL in sync with core filters (date/automation/interval/twilio)
  useEffect(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : searchParams.toString())
    params.set('from', fromISO)
    params.set('to', toISO)
    if (automationKey) params.set('automationKey', automationKey); else params.delete('automationKey')
    params.set('interval', interval)

    if (twilioToPhone) params.set('tl_to', twilioToPhone); else params.delete('tl_to')
    if (twilioStatus || twilioStatus === '') params.set('tl_status', twilioStatus)
    if (twilioDirection || twilioDirection === '') params.set('tl_dir', twilioDirection)

    const nextQs = params.toString()
    const currentQs = typeof window !== 'undefined' ? window.location.search.replace(/^\?/, '') : searchParams.toString()
    if (nextQs !== currentQs) {
      router.replace(`${pathname}?${nextQs}`, { scroll: false })
    }
  }, [fromISO, toISO, automationKey, interval, twilioToPhone, twilioStatus, twilioDirection, router, pathname])

  // Auth check
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const account = await getAccount()
        if (!alive) return
        if (!account) {
          router.replace('/login')
          return
        }
        try {
          const res = await fetch('/api/billing/me', { method: 'GET', credentials: 'include', cache: 'no-store' })
          if (res.ok) {
            const data = await res.json()
            const status = (data?.stripe?.status || '').toLowerCase()
            const subscribed = ['active','trialing','past_due'].includes(status)
            if (!subscribed) {
              router.replace('/subscription')
              return
            }
          } else {
            router.replace('/subscription')
            return
          }
        } catch {}
        setUser(account)
      } finally {
        if (alive) setAuthLoading(false)
      }
    })()
    return () => { alive = false }
  }, [router])

  // Load core analytics data
  async function fetchJSON(url) {
    const res = await fetch(url, { credentials: 'include', cache: 'no-store' })
    if (!res.ok) {
      if (res.status === 401) router.replace('/login')
      let err = {}
      try { err = await res.json() } catch {}
      throw new Error(err?.error || err?.message || `Request failed ${res.status}`)
    }
    return res.json()
  }

  async function loadCore() {
    setLoading(true)
    try {
      const intv = clampInterval(fromISO, toISO)
      setInterval(intv)

      const qs = new URLSearchParams()
      qs.set('from', fromISO)
      qs.set('to', toISO)
      if (automationKey) qs.set('automationKey', automationKey)

      const [sum, ts] = await Promise.all([
        fetchJSON(`/api/analytics/summary?${qs.toString()}`),
        fetchJSON(`/api/analytics/timeseries?${qs.toString()}&interval=${intv}`)
      ])
      setSummary(sum)
      setTimeseries(ts)
    } catch (e) {
      console.error('loadCore error', e)
    } finally {
      setLoading(false)
    }
  }

  // Load Twilio aggregates
  async function loadTwilioAgg() {
    try {
      const qs = new URLSearchParams()
      qs.set('from', fromISO)
      qs.set('to', toISO)
      if (twilioToPhone) qs.set('toPhone', twilioToPhone)
      if (twilioStatus) qs.set('status', twilioStatus)
      if (twilioDirection) qs.set('direction', twilioDirection)
      if (twilioLimit) qs.set('limit', String(twilioLimit))
      const data = await fetchJSON(`/api/analytics/twilio?${qs.toString()}`)
      setTwilioAgg(data)
    } catch (e) {
      console.warn('twilio agg error', e?.message || e)
      setTwilioAgg(null)
    }
  }

  // Load messages list (first page)
  async function loadMessages(first = true) {
    if (first) {
      setMessages([])
      setMessagesNextCursor(null)
    }
    setMessagesLoading(true)
    setMessagesError('')
    try {
      const qs = new URLSearchParams()
      if (msgStatus || msgStatus === '') qs.set('status', msgStatus)
      if (automationKey) qs.set('automationKey', automationKey)
      if (msgReply) qs.set('reply', msgReply)
      if (msgPhone) qs.set('phone', msgPhone)
      if (msgUserId) qs.set('userId', msgUserId)
      qs.set('from', fromISO)
      qs.set('to', toISO)
      qs.set('limit', String(limit))
      if (!first && messagesNextCursor) qs.set('cursor', messagesNextCursor)
      // Ask API to enrich with Twilio delivery status if possible
      qs.set('includeTwilio', '1')
      // Ask API to enrich with YES/NO reply for first_order follow-ups
      qs.set('includeReplies', '1')
      if (deliveredOnly) qs.set('delivered', '1')

      const data = await fetchJSON(`/api/analytics/messages?${qs.toString()}`)
      const items = Array.isArray(data?.items) ? data.items : []
      const mergeAndSort = (prev) => {
        const merged = first ? items : [...prev, ...items]
        return merged
      }
      setMessages(prev => mergeAndSort(prev))
      setMessagesNextCursor(data?.nextCursor || null)
    } catch (e) {
      setMessagesError(e?.message || 'Failed to load messages')
    } finally {
      setMessagesLoading(false)
    }
  }

  // Apply-only handler for message filters to avoid URL navigation on each keystroke
  function applyMessageFilters() {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : searchParams.toString())
    if (msgStatus || msgStatus === '') params.set('status', msgStatus)
    if (automationKey) params.set('automationKey', automationKey); else params.delete('automationKey')
    if (msgReply) params.set('reply', msgReply); else params.delete('reply')
    if (msgPhone) params.set('phone', msgPhone); else params.delete('phone')
    if (msgUserId) params.set('userId', msgUserId); else params.delete('userId')
    if (deliveredOnly) params.set('delivered', '1'); else params.delete('delivered')
    // If a Day is chosen, override global from/to to that day
    if (msgDay) {
      const dayStart = startOfDayISO(msgDay)
      const dayEnd = endOfDayISO(msgDay)
      setFromISO(dayStart)
      setToISO(dayEnd)
      params.set('from', dayStart)
      params.set('to', dayEnd)
    } else {
      params.set('from', fromISO)
      params.set('to', toISO)
    }
    params.set('interval', interval)
    params.set('sort', sortField)
    params.set('order', sortDir)
    const nextQs = params.toString()
    const currentQs = typeof window !== 'undefined' ? window.location.search.replace(/^\?/, '') : searchParams.toString()
    if (nextQs !== currentQs) {
      router.replace(`${pathname}?${nextQs}`, { scroll: false })
    }
    loadMessages(true)
  }

  // Refresh everything when core filters change (not typing in message filters)
  useEffect(() => {
    if (!user || authLoading) return
    loadCore()
    loadTwilioAgg()
    loadMessages(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, fromISO, toISO, automationKey])

  const automationOptions = useMemo(() => {
    const list = summary?.byAutomation || []
    return [{ key: '', name: 'All automations' }, ...list.map(a => ({ key: a.key, name: a.name || a.key }))]
  }, [summary])

  function quickRange(range) {
    const now = new Date()
    if (range === 'today') {
      setFromISO(startOfDayISO(now))
      setToISO(endOfDayISO(now))
    } else if (range === '7d') {
      const d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      setFromISO(startOfDayISO(d))
      setToISO(endOfDayISO(now))
    } else if (range === '30d') {
      const d = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      setFromISO(startOfDayISO(d))
      setToISO(endOfDayISO(now))
    } else if (range === '60d') {
      const d = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)
      setFromISO(startOfDayISO(d))
      setToISO(endOfDayISO(now))
    } else if (range === '90d') {
      const d = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
      setFromISO(startOfDayISO(d))
      setToISO(endOfDayISO(now))
    } else if (range === '120d') {
      const d = new Date(now.getTime() - 120 * 24 * 60 * 60 * 1000)
      setFromISO(startOfDayISO(d))
      setToISO(endOfDayISO(now))
    }
  }

  function toggleSort(field) {
    setSortField(prev => {
      if (prev === field) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
        return prev
      }
      setSortDir(field === 'when' ? 'desc' : 'asc')
      return field
    })
  }

  const sortedMessages = useMemo(() => {
    const arr = [...messages]
    const getWhen = (m) => {
      const t = m?.sentAt || m?.queuedAt || m?.$updatedAt || m?.$createdAt
      const d = t ? new Date(t) : null
      return d && !isNaN(d) ? d.getTime() : 0
    }
    const val = (m, field) => {
      switch (field) {
        case 'when': return getWhen(m)
        case 'status': return (m?.status || '').toString().toLowerCase()
        case 'delivered': return m?.deliveredAt ? new Date(m.deliveredAt).getTime() : 0
        case 'reply': return (m?.reply || '').toString().toLowerCase()
        case 'automation': return (m?.automationKey || '').toString().toLowerCase()
        case 'phone': return (m?.phone || '').toString()
        case 'user': return (m?.userId || '').toString()
        case 'error': return (m?.error || '').toString().toLowerCase()
        default: return ''
      }
    }
    arr.sort((a, b) => {
      const av = val(a, sortField)
      const bv = val(b, sortField)
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av
      }
      const res = String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? res : -res
    })
    return arr
  }, [messages, sortField, sortDir])

  const chartData = useMemo(() => {
    const buckets = timeseries?.buckets || []
    return buckets.map(b => ({
      name: fmtDay(b.t),
      sent: b.sent || 0,
      failed: b.failed || 0,
      queued: b.queued || 0
    }))
  }, [timeseries])

  // Map Twilio error code -> example error message (if any) from fetched items
  const errorMessageByCode = useMemo(() => {
    const map = {}
    const items = twilioAgg?.items || []
    for (const it of items) {
      const code = it?.errorCode
      const msg = it?.errorMessage
      if (code !== null && code !== undefined && msg && !map[String(code)]) {
        map[String(code)] = msg
      }
    }
    return map
  }, [twilioAgg])

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Analytics</h1>
        <p className="text-slate-400">Twilio-enriched SMS analytics with date filtering</p>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1">From</label>
            <input
              type="datetime-local"
              value={new Date(fromISO).toISOString().slice(0,16)}
              onChange={(e) => {
                const d = new Date(e.target.value)
                setFromISO(isNaN(d.getTime()) ? fromISO : d.toISOString())
              }}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Day</label>
            <input
              type="date"
              value={msgDay}
              onChange={(e) => setMsgDay(e.target.value)}
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">To</label>
            <input
              type="datetime-local"
              value={new Date(toISO).toISOString().slice(0,16)}
              onChange={(e) => {
                const d = new Date(e.target.value)
                setToISO(isNaN(d.getTime()) ? toISO : d.toISOString())
              }}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Interval</label>
            <select
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            >
              <option value="day">Day</option>
              <option value="hour">Hour</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Automation</label>
            <select
              value={automationKey}
              onChange={(e) => setAutomationKey(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            >
              {automationOptions.map(o => (
                <option key={o.key || 'all'} value={o.key}>{o.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={() => { loadCore(); loadTwilioAgg(); loadMessages(true) }} className="ml-auto px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700">Refresh</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPI title="Queued Now" value={summary?.totals?.queuedNow ?? '-'} />
        <KPI title="Sent 24h" value={summary?.totals?.sent24h ?? '-'} />
        <KPI title="Sent 7d" value={summary?.totals?.sent7d ?? '-'} />
        <KPI title="Failed 24h" value={summary?.totals?.failed24h ?? '-'} />
        <KPI title="Failed 7d" value={summary?.totals?.failed7d ?? '-'} />
        <KPI title="Opted-in" value={summary?.totals?.optInCount ?? '-'} />
      </div>

      {/* Twilio Aggregates */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-full sm:w-48">
            <label className="block text-sm text-slate-300 mb-1">Twilio To</label>
            <input
              type="text"
              value={twilioToPhone}
              onChange={(e) => setTwilioToPhone(e.target.value)}
              placeholder="+1..."
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            />
          </div>
          <div className="w-full sm:w-48">
            <label className="block text-sm text-slate-300 mb-1">Twilio Status</label>
            <select
              value={twilioStatus}
              onChange={(e) => setTwilioStatus(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            >
              <option value="">Any</option>
              <option value="accepted">accepted</option>
              <option value="queued">queued</option>
              <option value="sending">sending</option>
              <option value="sent">sent</option>
              <option value="failed">failed</option>
              <option value="delivered">delivered</option>
              <option value="undelivered">undelivered</option>
              <option value="received">received</option>
              <option value="receiving">receiving</option>
            </select>
          </div>
          <div className="w-full sm:w-56">
            <label className="block text-sm text-slate-300 mb-1">Twilio Direction</label>
            <select
              value={twilioDirection}
              onChange={(e) => setTwilioDirection(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            >
              <option value="">Any</option>
              <option value="inbound">inbound</option>
              <option value="outbound-api">outbound-api</option>
              <option value="outbound-reply">outbound-reply</option>
              <option value="outbound-call">outbound-call</option>
            </select>
          </div>
          <div className="w-full sm:w-40">
            <label className="block text-sm text-slate-300 mb-1">Twilio Limit</label>
            <input
              type="number"
              min={1}
              max={2000}
              value={twilioLimit}
              onChange={(e) => setTwilioLimit(Number(e.target.value || 500))}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            />
          </div>
          <button
            onClick={loadTwilioAgg}
            className="ml-auto px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded"
          >
            Refresh Twilio
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <KPI title="Twilio Messages" value={twilioAgg?.aggregates?.totalMessages ?? '-'} />
          <KPI title="Twilio Segments" value={twilioAgg?.aggregates?.totalSegments ?? '-'} />
          <KPI
            title="Twilio Cost"
            value={
              twilioAgg?.aggregates
                ? `${(twilioAgg.aggregates.totalCost || 0).toFixed(4)} ${twilioAgg.aggregates.priceUnit || 'USD'}`
                : '-'
            }
          />
          <KPI title="Statuses" value={Object.keys(twilioAgg?.aggregates?.byStatus || {}).length || '-'} />
        </div>

        {/* Error Code Breakdown */}
        {twilioAgg?.aggregates?.byErrorCode && Object.keys(twilioAgg.aggregates.byErrorCode).length > 0 && (
          <div className="mt-4">
            <h3 className="text-slate-200 font-medium mb-2">Twilio Error Codes</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-300">
                    <th className="py-2 pr-4">Error Code</th>
                    <th className="py-2 pr-4">Count</th>
                    <th className="py-2 pr-4">Message</th>
                  </tr>
                </thead>
                <tbody className="text-slate-200">
                  {Object.entries(twilioAgg.aggregates.byErrorCode).map(([code, count]) => (
                    <tr key={code} className="border-t border-slate-700">
                      <td className="py-2 pr-4">{code}</td>
                      <td className="py-2 pr-4">{count}</td>
                      <td className="py-2 pr-4 text-slate-400">{errorMessageByCode[code] || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Timeseries Chart */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
        <h2 className="text-white font-semibold mb-3">Messages Over Time</h2>
        <div className="w-full h-72">
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' }} />
              <Legend />
              <Line type="monotone" dataKey="sent" stroke="#22c55e" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="queued" stroke="#60a5fa" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* By Automation */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
        <h2 className="text-white font-semibold mb-3">By Automation</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-300">
                <th className="py-2 pr-6">Automation</th>
                <th className="py-2 pr-6">Queued</th>
                <th className="py-2 pr-6">Sent</th>
                <th className="py-2 pr-6">Failed</th>
              </tr>
            </thead>
            <tbody className="text-slate-200">
              {(summary?.byAutomation || []).map(row => (
                <tr key={row.key} className="border-t border-slate-700">
                  <td className="py-2 pr-6">{row.name || row.key}</td>
                  <td className="py-2 pr-6">{row.queued}</td>
                  <td className="py-2 pr-6">{row.sent7d}</td>
                  <td className="py-2 pr-6">{row.failed7d}</td>
                </tr>
              ))}
              {(!summary?.byAutomation || summary.byAutomation.length === 0) && (
                <tr><td className="py-3 text-slate-400" colSpan={4}>No automation data.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Lists */}
      <div className="grid grid-cols-1 gap-4">
        <RecentList title="Recent Sent" items={summary?.recent?.sent} timeKey="sentAt" />
        <RecentList title="Recent Queued" items={summary?.recent?.queued} timeKey="queuedAt" />
      </div>

      {/* Messages Table with Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-sm text-slate-300 mb-1">Status</label>
            <select
              value={msgStatus}
              onChange={(e) => setMsgStatus(e.target.value)}
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            >
              <option value="">Any</option>
              <option value="queued">queued</option>
              <option value="sent">sent</option>
              <option value="failed">failed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Reply</label>
            <select
              value={msgReply}
              onChange={(e) => setMsgReply(e.target.value)}
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            >
              <option value="">Any</option>
              <option value="yes">YES</option>
              <option value="no">NO</option>
            </select>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <input
              id="deliveredOnly"
              type="checkbox"
              checked={deliveredOnly}
              onChange={(e) => setDeliveredOnly(e.target.checked)}
              className="h-4 w-4 text-green-500 bg-slate-700 border-slate-600 rounded"
            />
            <label htmlFor="deliveredOnly" className="text-sm text-slate-300">Delivered only</label>
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Phone</label>
            <input
              type="text"
              value={msgPhone}
              onChange={(e) => setMsgPhone(e.target.value)}
              placeholder="+1..."
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">User ID</label>
            <input
              type="text"
              value={msgUserId}
              onChange={(e) => setMsgUserId(e.target.value)}
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Page Size</label>
            <input
              type="number"
              min={1}
              max={100}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value || 50))}
              className="px-3 py-2 w-28 bg-slate-700 border border-slate-600 rounded-md text-white"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => quickRange('30d')} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded">30d</button>
            <button onClick={() => quickRange('60d')} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded">60d</button>
            <button onClick={() => quickRange('90d')} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded">90d</button>
            <button onClick={() => quickRange('120d')} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded">120d</button>
            <button
              onClick={applyMessageFilters}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded"
            >
              Apply
            </button>
          </div>
        </div>

        <div className="overflow-x-auto mt-4">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-300 select-none">
                <th className="py-2 pr-4">
                  <button onClick={() => toggleSort('when')} className="inline-flex items-center gap-1 hover:text-slate-200">
                    When {sortField === 'when' && (sortDir === 'asc' ? '▲' : '▼')}
                  </button>
                </th>
                <th className="py-2 pr-4">
                  <button onClick={() => toggleSort('status')} className="inline-flex items-center gap-1 hover:text-slate-200">
                    Status {sortField === 'status' && (sortDir === 'asc' ? '▲' : '▼')}
                  </button>
                </th>
                <th className="py-2 pr-4">
                  <button onClick={() => toggleSort('delivered')} className="inline-flex items-center gap-1 hover:text-slate-200">
                    Delivered {sortField === 'delivered' && (sortDir === 'asc' ? '▲' : '▼')}
                  </button>
                </th>
                <th className="py-2 pr-4">
                  <button onClick={() => toggleSort('reply')} className="inline-flex items-center gap-1 hover:text-slate-200">
                    Reply {sortField === 'reply' && (sortDir === 'asc' ? '▲' : '▼')}
                  </button>
                </th>
                <th className="py-2 pr-4">
                  <button onClick={() => toggleSort('automation')} className="inline-flex items-center gap-1 hover:text-slate-200">
                    Automation {sortField === 'automation' && (sortDir === 'asc' ? '▲' : '▼')}
                  </button>
                </th>
                <th className="py-2 pr-4">
                  <button onClick={() => toggleSort('phone')} className="inline-flex items-center gap-1 hover:text-slate-200">
                    Phone {sortField === 'phone' && (sortDir === 'asc' ? '▲' : '▼')}
                  </button>
                </th>
                <th className="py-2 pr-4">
                  <button onClick={() => toggleSort('user')} className="inline-flex items-center gap-1 hover:text-slate-200">
                    User {sortField === 'user' && (sortDir === 'asc' ? '▲' : '▼')}
                  </button>
                </th>
                <th className="py-2 pr-4">
                  <button onClick={() => toggleSort('error')} className="inline-flex items-center gap-1 hover:text-slate-200">
                    Error {sortField === 'error' && (sortDir === 'asc' ? '▲' : '▼')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="text-slate-200">
              {sortedMessages.map(m => {
                const when = m.sentAt || m.queuedAt || m.$updatedAt || m.$createdAt
                return (
                  <tr key={m.$id} className="border-t border-slate-700">
                    <td className="py-2 pr-4 whitespace-nowrap">{fmtDateTime(when)}</td>
                    <td className={cls('py-2 pr-4', m.status === 'failed' ? 'text-red-400' : m.status === 'sent' ? 'text-green-400' : 'text-blue-300')}>
                      {m.status}
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {m.deliveredAt ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-green-400">✔</span>
                          <span className="text-slate-300">{fmtDateTime(m.deliveredAt)}</span>
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {m.reply ? (
                        <span className={cls('px-2 py-0.5 rounded text-xs font-medium', m.reply.toLowerCase() === 'yes' ? 'bg-green-900/40 text-green-300' : 'bg-red-900/40 text-red-300')}>
                          {String(m.reply).toUpperCase()}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-2 pr-4">{m.automationKey || '-'}</td>
                    <td className="py-2 pr-4">{m.phone || '-'}</td>
                    <td className="py-2 pr-4">{m.userId || '-'}</td>
                    <td className="py-2 pr-4 text-slate-400">{m.error || '-'}</td>
                  </tr>
                )
              })}
              {!messagesLoading && messages.length === 0 && (
                <tr><td colSpan={8} className="py-3 text-slate-400">No messages found for this filter.</td></tr>
              )}
            </tbody>
          </table>
          {messagesError && <div className="mt-2 text-red-300">{messagesError}</div>}
          <div className="flex justify-between items-center mt-3">
            <div className="text-slate-400 text-sm">{messages.length} loaded</div>
            <div className="flex items-center gap-2">
              <button
                disabled={messagesLoading}
                onClick={() => loadMessages(true)}
                className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded disabled:opacity-50"
              >
                Refresh
              </button>
              <button
                disabled={!messagesNextCursor || messagesLoading}
                onClick={() => loadMessages(false)}
                className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50"
              >
                {messagesLoading ? 'Loading...' : 'Load more'}
              </button>
            </div>
          </div>
        </div>
      </div>

      
    </div>
  )
}

function KPI({ title, value }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
      <div className="text-slate-400 text-sm">{title}</div>
      <div className="text-2xl font-semibold text-white mt-1">{value}</div>
    </div>
  )
}

function RecentList({ title, items, timeKey }) {
  const list = Array.isArray(items) ? items : []
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
      <h2 className="text-white font-semibold mb-3">{title}</h2>
      {/* Internal scroll area */}
      <div className="overflow-x-auto overflow-y-auto max-h-96 pr-1">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-slate-800 z-10">
            <tr className="text-left text-slate-300">
              <th className="py-2 pr-4">When</th>
              <th className="py-2 pr-4">Automation</th>
              <th className="py-2 pr-4">Phone</th>
              <th className="py-2 pr-4">User</th>
            </tr>
          </thead>
          <tbody className="text-slate-200">
            {list.map((m) => (
              <tr key={m.$id} className="border-t border-slate-700">
                <td className="py-2 pr-4">{fmtDateTime(m[timeKey] || m.$updatedAt || m.$createdAt)}</td>
                <td className="py-2 pr-4">{m.automationKey || '-'}</td>
                <td className="py-2 pr-4">{m.phone || '-'}</td>
                <td className="py-2 pr-4">{m.userId || '-'}</td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td className="py-3 text-slate-400" colSpan={4}>No recent data.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function AnalyticsPage() {
  return (
    <Suspense fallback={<PageFallback />}>
      <AnalyticsContent />
    </Suspense>
  )
}

function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
        <p className="text-slate-400">Loading analytics...</p>
      </div>
    </div>
  )
}